import { EntityType, Perspective } from '@portal/pkg-api/gen/navigator'
import type { ScopedEnablements } from '@portal/pkg-authorization'
import { isViewableEntityType } from '@navigator/entityTypeAccess'

// Picks the entity type to land on when the URL doesn't specify one. Normally
// this is the first type the caller can view, but if they have pinned tabs and
// the default isn't among them, we prefer their first viewable pinned tab.
// A stored pin for a type they can no longer view is skipped.
export function getDefaultEntityTypeCode(
  entityTypes: EntityType[],
  pinnedTabs: string[],
  scopedEnablements: ScopedEnablements,
): string | undefined {
  const viewable = entityTypes.filter((entityType) =>
    isViewableEntityType(entityType, scopedEnablements),
  )
  const viewableCodes = new Set(viewable.map((entityType) => entityType.code))
  const first = viewable[0]?.code
  if (first && pinnedTabs.length > 0 && !pinnedTabs.includes(first)) {
    return pinnedTabs.find((code) => viewableCodes.has(code)) ?? first
  }
  return first
}

// Builds the canonical open-tabs list for the URL. Invariants:
//   - viewable pinned tabs are always present (and ordered first, matching the tab strip)
//   - the active entity type is present when the caller can view it
//   - codes the caller cannot view are dropped, including a stored pin or an
//     active code left over after View Entity was removed
export function buildCanonicalOpenTabs(
  urlOpenTabs: string,
  entityTypeCode: string | undefined,
  pinnedTabs: string[],
  entityTypes: EntityType[],
  scopedEnablements: ScopedEnablements,
): string[] {
  const validCodes = new Set(
    entityTypes
      .filter((entityType) => isViewableEntityType(entityType, scopedEnablements))
      .map((et) => et.code),
  )
  const fromUrl = urlOpenTabs ? urlOpenTabs.split('-').filter((c) => validCodes.has(c)) : []
  const validPinned = pinnedTabs.filter((c) => validCodes.has(c))
  const merged = [...validPinned, ...fromUrl.filter((c) => !validPinned.includes(c))]
  if (entityTypeCode && validCodes.has(entityTypeCode) && !merged.includes(entityTypeCode)) {
    merged.push(entityTypeCode)
  }
  return merged
}

// Resolves the URL's entityType to a canonical value: keep it when the caller
// can view that type, otherwise fall back to the default. A code they cannot
// view is treated as missing so the loader can replace the URL.
export function resolveCanonicalEntityType(
  entityType: string | undefined,
  entityTypes: EntityType[],
  pinnedTabs: string[],
  scopedEnablements: ScopedEnablements,
): string | undefined {
  const valid =
    entityType &&
    entityTypes.some((et) => et.code === entityType && isViewableEntityType(et, scopedEnablements))
      ? entityType
      : undefined
  return valid ?? getDefaultEntityTypeCode(entityTypes, pinnedTabs, scopedEnablements)
}

// Maps a perspective to the entity type code it belongs to. Returns undefined
// if the perspective has no entity types or refers to one that is missing,
// disabled, or not viewable. The grid loader then drops `p` and stays on the grid.
export function perspectiveEntityTypeCode(
  perspective: Perspective,
  entityTypes: EntityType[],
  scopedEnablements: ScopedEnablements,
): string | undefined {
  const id = perspective.entityTypes[0]
  if (id === undefined) return undefined
  const et = entityTypes.find((e) => e.id === id && isViewableEntityType(e, scopedEnablements))
  return et?.code
}
